import { writeStoredSession } from "./accountLinks";

const API_BASE = "https://qr.billsarthi.in/public/api";
const MASTER_MOBILE = String(
  process.env.REACT_APP_MASTER_MOBILE || "9988777409"
).replace(/\D/g, "");
const MASTER_OTP_REQUEST_ENDPOINT =
  process.env.REACT_APP_MASTER_OTP_REQUEST_ENDPOINT ||
  `${API_BASE}/auth/forgot/request`;
const MASTER_OTP_VERIFY_ENDPOINT =
  process.env.REACT_APP_MASTER_OTP_VERIFY_ENDPOINT ||
  `${API_BASE}/verifyphone`;
const MASTER_USERS_ENDPOINT =
  process.env.REACT_APP_MASTER_USERS_ENDPOINT || `${API_BASE}/masterkey/users`;

export const USER_DATA_KEY = "userData";
export const IMPERSONATION_META_KEY = "impersonationMeta";
export const ORIGINAL_USER_DATA_KEY = "originalUserData";
export const MASTER_OTP_KEY = "masterOtpVerified";
export const AUDIT_LOG_KEY = "securityAuditLog";

const truthy = (v) => v === 1 || v === "1" || v === true;
const falsy = (v) => v === 0 || v === "0" || v === false;

const parseJson = (raw, fallback = null) => {
  if (!raw) return fallback;
  try {
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
};

const appendAuditLog = (event, details = {}) => {
  const current = parseJson(localStorage.getItem(AUDIT_LOG_KEY), []);
  const next = Array.isArray(current) ? current : [];
  next.unshift({
    id: `${Date.now()}-${Math.random().toString(16).slice(2, 8)}`,
    at: new Date().toISOString(),
    event,
    details,
  });
  localStorage.setItem(AUDIT_LOG_KEY, JSON.stringify(next.slice(0, 200)));
};

export const getStoredSession = () =>
  parseJson(localStorage.getItem(USER_DATA_KEY), {});

export const setStoredSession = (session) => {
  writeStoredSession(session || {});
};

export const getStoredUser = () => getStoredSession()?.data || null;

export const getOriginalSession = () =>
  parseJson(localStorage.getItem(ORIGINAL_USER_DATA_KEY), null);

export const getImpersonationMeta = () =>
  parseJson(localStorage.getItem(IMPERSONATION_META_KEY), null);

export const isImpersonating = () => Boolean(getImpersonationMeta()?.active);

export const clearImpersonationStorage = () => {
  localStorage.removeItem(IMPERSONATION_META_KEY);
  localStorage.removeItem(ORIGINAL_USER_DATA_KEY);
  localStorage.removeItem(MASTER_OTP_KEY);
};

const getMobileFromUser = (user = {}) =>
  String(
    user?.mobile_number ||
      user?.mobile ||
      user?.phone ||
      user?.phonenumber ||
      user?.contact_number ||
      ""
  ).replace(/\D/g, "");

const getMasterOtpMeta = () => parseJson(localStorage.getItem(MASTER_OTP_KEY), null);

const isMasterMobile = (mobile) => String(mobile || "").replace(/\D/g, "") === MASTER_MOBILE;

export const canAttemptMasterOtp = (user = getStoredUser()) =>
  Boolean(user && isMasterMobile(getMobileFromUser(user)));

export const isMasterSession = (user = getStoredUser()) => {
  if (!user) return false;
  const role = String(user?.role || user?.user_role || "").toLowerCase();
  return user?.id === "master-user" || role === "master";
};

export const isMasterOtpVerified = (user = getStoredUser()) => {
  if (!canAttemptMasterOtp(user)) return false;
  const meta = getMasterOtpMeta();
  return Boolean(meta?.verified && isMasterMobile(meta?.mobile));
};

const setMasterOtpVerified = (mobile) => {
  localStorage.setItem(
    MASTER_OTP_KEY,
    JSON.stringify({
      verified: true,
      mobile: String(mobile || "").replace(/\D/g, ""),
      verifiedAt: new Date().toISOString(),
    })
  );
};

export const requestMasterOtp = async (user = getStoredUser()) => {
  const mobile = getMobileFromUser(user);
  if (!isMasterMobile(mobile)) {
    throw new Error("Master OTP is only available for the configured master number.");
  }

  const response = await fetch(MASTER_OTP_REQUEST_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      mobile_number: mobile,
      phone: mobile,
    }),
  });

  let json = {};
  try {
    json = await response.json();
  } catch {
    // no-op
  }

  if (!response.ok) {
    throw new Error(readMessage(json, "Unable to send OTP."));
  }

  appendAuditLog("master_otp_requested", {
    mobile,
    userId: user?.id || null,
  });

  return { message: readMessage(json, "OTP sent to master mobile number.") };
};

export const verifyMasterOtp = async ({ otp, user = getStoredUser() }) => {
  const mobile = getMobileFromUser(user);
  if (!isMasterMobile(mobile)) {
    throw new Error("Master OTP is only available for the configured master number.");
  }
  if (!otp || String(otp).trim().length < 4) {
    throw new Error("Please enter a valid OTP.");
  }

  const response = await fetch(MASTER_OTP_VERIFY_ENDPOINT, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      phone: mobile,
      mobile_number: mobile,
      otp: String(otp).trim(),
    }),
  });

  let json = {};
  try {
    json = await response.json();
  } catch {
    // no-op
  }

  if (!response.ok) {
    throw new Error(readMessage(json, "OTP verification failed."));
  }

  setMasterOtpVerified(mobile);
  appendAuditLog("master_otp_verified", {
    mobile,
    userId: user?.id || null,
  });
  return { message: readMessage(json, "Master OTP verified successfully.") };
};

const readWebAccessFlag = (session = getStoredSession()) => {
  const user = session?.data || {};
  return (
    user?.isWebAllowed ??
    user?.isweballowed ??
    session?.isWebAllowed ??
    session?.isweballowed
  );
};

export const hasWebAccessFromStorage = () => {
  const session = getStoredSession();
  const hasSession = Boolean(session && Object.keys(session).length);
  if (!hasSession) return false;

  if (isImpersonating()) return true;

  const rawWebAccess = readWebAccessFlag(session);
  if (rawWebAccess == null) return true;
  return !falsy(rawWebAccess);
};

export const canManageImpersonation = (user = getStoredUser()) => {
  if (!user) return false;
  const role = String(user?.role || user?.user_role || "").toLowerCase();
  const roleAllowed = ["owner", "admin", "super_admin", "superadmin", "master"].includes(
    role
  );
  const flagAllowed = [
    user?.can_impersonate,
    user?.canImpersonate,
    user?.allow_impersonation,
    user?.is_admin,
    user?.issuperadmin,
  ].some(truthy);

  const masterAllowed = isMasterOtpVerified(user);

  return roleAllowed || flagAllowed || masterAllowed;
};

const getAuthToken = (session = getStoredSession()) => {
  const fromRoot =
    session?.token ||
    session?.access_token ||
    session?.auth_token ||
    session?.jwt ||
    session?.bearer_token;
  const fromUser =
    session?.data?.token ||
    session?.data?.access_token ||
    session?.data?.auth_token ||
    session?.data?.jwt;

  return fromRoot || fromUser || null;
};

const buildAuthHeaders = (session) => {
  const token = getAuthToken(session);
  if (!token) return {};
  return {
    Authorization: `Bearer ${token}`,
    "X-Auth-Token": token,
  };
};

const normalizeSession = (json) => {
  const candidates = [
    json?.session,
    json?.data?.session,
    json?.impersonated_session,
    json?.data?.impersonated_session,
    json?.data,
    json,
  ];

  const found = candidates.find((item) => item && typeof item === "object");
  if (!found) return null;

  // If API returns only user object, wrap it to match app's expected shape.
  if (!Object.prototype.hasOwnProperty.call(found, "data")) {
    return { data: found };
  }

  return found;
};

const readMessage = (json, fallback) =>
  json?.message || json?.error || json?.data?.message || fallback;

export const startImpersonation = async ({
  targetMobile,
  targetUserId,
  targetRestaurantId,
  reason,
}) => {
  const adminSession = getStoredSession();
  const adminUser = adminSession?.data || {};

  if (!canManageImpersonation(adminUser)) {
    throw new Error("Your account is not allowed to impersonate users.");
  }
  if (isImpersonating()) {
    throw new Error("Stop the current impersonation before starting a new one.");
  }

  const payload = {
    admin_id: adminUser?.id || null,
    target_mobile_number: targetMobile || null,
    target_user_id: targetUserId || null,
    target_restaurant_id: targetRestaurantId || null,
    reason: reason || "Support request",
  };

  const response = await fetch(`${API_BASE}/admin/impersonation/start`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...buildAuthHeaders(adminSession),
    },
    body: JSON.stringify(payload),
  });

  let json = {};
  try {
    json = await response.json();
  } catch {
    // no-op
  }

  if (!response.ok) {
    throw new Error(readMessage(json, "Unable to start impersonation."));
  }

  const nextSession = normalizeSession(json);
  if (!nextSession?.data) {
    throw new Error("Impersonation started but no session payload was returned by API.");
  }

  localStorage.setItem(ORIGINAL_USER_DATA_KEY, JSON.stringify(adminSession));
  writeStoredSession(nextSession, { preserveAccountLinkContext: false });

  const target = nextSession?.data || {};
  const meta = {
    active: true,
    startedAt: new Date().toISOString(),
    impersonationId:
      json?.impersonation_id || json?.data?.impersonation_id || json?.data?.id || null,
    admin: {
      id: adminUser?.id || null,
      name: adminUser?.name || "Admin",
      mobile: adminUser?.mobile_number || adminUser?.mobile || null,
      role: adminUser?.role || null,
    },
    target: {
      id: target?.id || targetUserId || null,
      name: target?.name || target?.restaurant_name || "User",
      mobile: target?.mobile_number || target?.mobile || targetMobile || null,
      restaurantId: target?.restaurantid || target?.id || targetRestaurantId || null,
    },
    reason: payload.reason,
  };

  localStorage.setItem(IMPERSONATION_META_KEY, JSON.stringify(meta));
  appendAuditLog("impersonation_started", {
    adminId: meta?.admin?.id || null,
    targetId: meta?.target?.id || null,
    mode: meta?.mode || "api",
    reason: meta?.reason || null,
  });
  return { meta, message: readMessage(json, "Impersonation started.") };
};

export const stopImpersonation = async () => {
  const meta = getImpersonationMeta();
  const originalSession = getOriginalSession();
  const currentSession = getStoredSession();

  if (!meta?.active && !originalSession) {
    clearImpersonationStorage();
    return { message: "No active impersonation found." };
  }

  let apiError = null;
  if (meta?.mode !== "local_masterkey") {
    try {
      const response = await fetch(`${API_BASE}/admin/impersonation/stop`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...buildAuthHeaders(originalSession || currentSession),
        },
        body: JSON.stringify({
          impersonation_id: meta?.impersonationId || null,
          admin_id: originalSession?.data?.id || meta?.admin?.id || null,
          impersonated_user_id: currentSession?.data?.id || meta?.target?.id || null,
        }),
      });

      if (!response.ok) {
        let json = {};
        try {
          json = await response.json();
        } catch {
          // no-op
        }
        apiError = new Error(readMessage(json, "Unable to stop impersonation on server."));
      }
    } catch (err) {
      apiError = new Error(err?.message || "Unable to stop impersonation on server.");
    }
  }

  if (originalSession && Object.keys(originalSession).length) {
    writeStoredSession(originalSession, { preserveAccountLinkContext: false });
  } else {
    localStorage.removeItem(USER_DATA_KEY);
  }
  clearImpersonationStorage();

  if (apiError) {
    throw new Error(`${apiError.message} Local session has been restored.`);
  }

  appendAuditLog("impersonation_stopped", {
    adminId: originalSession?.data?.id || meta?.admin?.id || null,
    targetId: currentSession?.data?.id || meta?.target?.id || null,
    mode: meta?.mode || "api",
  });

  return { message: "Returned to your admin session." };
};

export const fetchMasterUsers = async () => {
  const response = await fetch(MASTER_USERS_ENDPOINT);
  let json = {};
  try {
    json = await response.json();
  } catch {
    // no-op
  }

  if (!response.ok || json?.success === false) {
    throw new Error(readMessage(json, "Unable to fetch master users."));
  }

  return Array.isArray(json?.data) ? json.data : [];
};

export const startLocalMasterSwitch = (entry) => {
  const payload = entry?.login_payload;
  if (!payload || typeof payload !== "object") {
    throw new Error("Invalid master user payload.");
  }

  const current = getStoredSession();
  if (!isImpersonating()) {
    localStorage.setItem(ORIGINAL_USER_DATA_KEY, JSON.stringify(current || {}));
  }

  writeStoredSession(payload, { preserveAccountLinkContext: false });
  const targetData = payload?.data || {};
  localStorage.setItem(
    IMPERSONATION_META_KEY,
    JSON.stringify({
      active: true,
      mode: "local_masterkey",
      startedAt: new Date().toISOString(),
      admin: {
        id: current?.data?.id || null,
        name: current?.data?.name || "Master Admin",
      },
      target: {
        id: targetData?.id || entry?.id || null,
        name:
          targetData?.name ||
          targetData?.restaurant_name ||
          entry?.display_name ||
          "User",
        mobile: targetData?.mobile_number || entry?.mobile_number || null,
      },
      reason: "Master key account switch",
    })
  );
  appendAuditLog("master_local_switch", {
    adminId: current?.data?.id || null,
    targetId: targetData?.id || entry?.id || null,
    targetMobile: targetData?.mobile_number || entry?.mobile_number || null,
  });
};
